import { pdfFooterHeightPx } from '../modules/docs/utils/pdfLetterhead';
import React, { useMemo, useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Table, 
  IconButton,
  Stack,
  Button,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  Option,
  FormControl,
  FormLabel,
  Input,
  Alert
} from '@mui/joy';
import { IconWrapper } from '../components/IconWrapper';
import { FaChevronDown, FaChevronRight, FaDownload, FaFileInvoice } from 'react-icons/fa';
import { cisService } from '../services/cisService';
import type { CISSummary, CISMonthlyPeriod, CISInvoiceItem } from '../types/cis.types';
import { documentsService } from '../modules/docs/services/documents.service';
import { pdfExportConfigService } from '../modules/docs/services/pdfExportConfig.service';
import { documentTemplateService } from '../modules/docs/services/documentTemplate.service';
import type { DocumentKind } from '../modules/docs/types/documents.types';
import { cleanupRemainingTemplateVariables } from '../utils/graphService';

const currency = (n: number) => `£${(n || 0).toFixed(2)}`;
const money = (n: number) => `£${(Number.isFinite(n) ? n : 0).toFixed(2)}`;
const normalizeItemType = (itemType?: string): 'labour' | 'material' => (
  itemType === 'material' ? 'material' : 'labour'
);

function getLineItemAmount(item: CISInvoiceItem['line_items'][number]): number {
  if (typeof item.total === 'number' && Number.isFinite(item.total)) return Number(item.total);
  return Number(item.quantity || 0) * Number(item.unit_price || 0);
}

function getInvoiceLabourMaterials(invoice: CISInvoiceItem): { labour: number; materials: number } {
  const lines = Array.isArray(invoice.line_items) ? invoice.line_items : [];
  if (lines.length === 0) {
    return { labour: Number(invoice.subtotal || 0), materials: 0 };
  }

  const hasExplicitType = lines.some((line) => line && typeof line.item_type === 'string');
  if (!hasExplicitType) {
    // Legacy invoices: no item type means historic single bucket (labour).
    return { labour: Number(invoice.subtotal || 0), materials: 0 };
  }

  let labour = 0;
  let materials = 0;
  for (const line of lines) {
    const amount = getLineItemAmount(line);
    if (normalizeItemType(line.item_type) === 'material') {
      materials += amount;
    } else {
      labour += amount;
    }
  }
  return { labour, materials };
}

function tryExtractPostcode(address?: string | null): string {
  const a = String(address || '').trim();
  if (!a) return '';
  // Very loose UK postcode matcher; returns last match.
  const m = a.match(/([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})/gi);
  if (!m || m.length === 0) return '';
  return String(m[m.length - 1] || '').toUpperCase().replace(/\s+/g, ' ').trim();
}

function escapeHtml(s: string): string {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function toAbsoluteAssetUrl(pathOrUrl?: string | null): string {
  const raw = String(pathOrUrl || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw) || raw.startsWith('data:')) return raw;
  try {
    return new URL(raw, window.location.origin).toString();
  } catch {
    return raw;
  }
}

function buildPdfHeaderFooterOptions(opts: { headerImagePath?: string | null; footerImagePath?: string | null; footerHeightPx?: number }) {
  const headerSrc = toAbsoluteAssetUrl(opts.headerImagePath || '');
  const footerSrc = toAbsoluteAssetUrl(opts.footerImagePath || '');
  const sideMarginPx = 36;
  const headerHeightPx = 110;
  const footerHeightPx = opts.footerHeightPx ?? 66;
  const headerGapPx = 24;
  const footerGapPx = 14;
  const topMarginPx = headerSrc ? (headerHeightPx + headerGapPx) : sideMarginPx;
  const bottomMarginPx = footerSrc ? (footerHeightPx + footerGapPx) : sideMarginPx;
  const headerTemplate = headerSrc
    ? `<div style="width:100%;height:${headerHeightPx}px;padding:0 ${sideMarginPx}px;box-sizing:border-box;">
<img src="${headerSrc}" alt="PDF Header" style="display:block;width:100%;height:100%;object-fit:contain;" />
</div>`
    : '';
  const footerTemplate = footerSrc
    ? `<div style="width:100%;height:${footerHeightPx}px;padding:0 ${sideMarginPx}px;box-sizing:border-box;">
<img src="${footerSrc}" alt="PDF Footer" style="display:block;width:100%;height:100%;object-fit:contain;" />
</div>`
    : '';

  return {
    displayHeaderFooter: !!(headerTemplate || footerTemplate),
    headerTemplate,
    footerTemplate,
    margin: {
      top: `${topMarginPx}px`,
      right: `${sideMarginPx}px`,
      bottom: `${bottomMarginPx}px`,
      left: `${sideMarginPx}px`,
    },
  };
}

function isoToUk(iso?: string | null): string {
  const s = String(iso || '').trim();
  if (!s) return '';
  const parts = s.split('-');
  if (parts.length !== 3) return s;
  const [yyyy, mm, dd] = parts;
  if (!yyyy || !mm || !dd) return s;
  return `${dd.padStart(2, '0')}/${mm.padStart(2, '0')}/${yyyy}`;
}

/**
 * Renders the CIS payment and deduction statement for one subcontractor and tax month from the
 * organisation's CISCertificate template, and downloads it as a PDF.
 */
export async function downloadCisCertificate({
  period,
  subcontractorId,
  verificationNumber,
  organisationId,
}: {
  period: CISMonthlyPeriod;
  subcontractorId: string;
  verificationNumber: string;
  organisationId?: string;
}): Promise<void> {
  const invoices = (period.invoices || []).filter(i => String(i.subcontractor_id) === String(subcontractorId));
  if (invoices.length === 0) {
    throw new Error('No CIS invoices found for that subcontractor in this period.');
  }

  // Pick "subcontractor details" from the first invoice (cisService maps these through).
  const first = invoices[0];
  const subcontractorName = first.subcontractor_company || first.subcontractor_name || 'Subcontractor';
  const subcontractorTradingName = first.subcontractor_company && first.subcontractor_name ? first.subcontractor_name : '';
  const subcontractorAddress = first.subcontractor_address || '';

  const totalLabour = invoices.reduce((s, i) => s + getInvoiceLabourMaterials(i).labour, 0);
  const totalMaterials = invoices.reduce((s, i) => s + getInvoiceLabourMaterials(i).materials, 0);
  const totalGross = totalLabour + totalMaterials;
  const totalCis = invoices.reduce((s, i) => s + Number(i.cis_deduction || 0), 0);
  const totalNet = invoices.reduce((s, i) => s + Number(i.net_total || 0), 0);

  const cisRate = Number(first.cis_percent || 0);
  const rate0 = cisRate === 0;
  const rate20 = cisRate === 20;
  const rate30 = cisRate === 30;

  const workRowsHtml = invoices.map(inv => {
    const descParts = [
      inv.invoice_number ? `Invoice #${escapeHtml(String(inv.invoice_number))}` : 'Invoice',
      inv.invoice_date ? escapeHtml(String(inv.invoice_date)) : '',
      inv.description ? escapeHtml(String(inv.description)) : ''
    ].filter(Boolean);
    const desc = descParts.join(' • ');
    return `<tr><td>${desc}</td><td style="text-align:right;">${money(Number(inv.subtotal || 0))}</td></tr>`;
  }).join('\n');

  // Pull PDF config + template for CISCertificate (prefer the active org used elsewhere in UI).
  const orgs = await documentsService.listOrganisations();
  const rememberedOrgId = (() => {
    try { return localStorage.getItem('subcontractorInvoiceSelectedOrgId') || ''; } catch { return ''; }
  })();
  const orgId =
    String(organisationId || '').trim() ||
    (rememberedOrgId && (orgs || []).some(o => o.id === rememberedOrgId) ? rememberedOrgId : '') ||
    (orgs?.[0]?.id || '');
  const org0 = (orgs || []).find(o => o.id === orgId) || orgs?.[0];
  const docType: DocumentKind = 'CISCertificate';
  const [cfg, tpl] = await Promise.all([
    orgId ? pdfExportConfigService.getConfigByOrganisation(orgId, docType) : Promise.resolve(null),
    orgId ? documentTemplateService.getTemplate(orgId, docType) : Promise.resolve(null),
  ]);
  const template = (tpl?.template && String(tpl.template).trim().length > 0)
    ? String(tpl.template)
    : await documentTemplateService.getDefaultTemplate(docType);

  const companyName = cfg?.companyName || 'Keystone Glazing';
  const companyAddress = cfg?.companyAddress || '';
  const contractorPostcode = tryExtractPostcode(companyAddress);
  const companyTradingName = (cfg as any)?.companyTradingName || org0?.name || '';
  const companyUtr = (cfg as any)?.companyUtr || '';

  const generationDate = new Date().toLocaleDateString('en-GB');
  const paymentDate = `${isoToUk(period.period_start)} – ${isoToUk(period.period_end)}`.trim();
  const taxMonthLabel = period.period_label || `${period.period_start} to ${period.period_end}`;
  const invoiceCoverage = invoices
    .map((inv) => String(inv.invoice_number || '').trim())
    .filter(Boolean)
    .join(', ');

  const replacements: Record<string, string> = {
    taxMonthLabel,
    contractorName: companyName,
    contractorTradingName: companyTradingName,
    contractorUtr: companyUtr,
    contractorCompanyReg: cfg?.companyRegistrationNumber || '',
    contractorAddress: companyAddress,
    contractorPostcode,
    subcontractorName,
    subcontractorTradingName,
    subcontractorUtr: first.subcontractor_utr_number || '',
    subcontractorNi: first.subcontractor_ni_number || '',
    subcontractorCompanyReg: '',
    subcontractorAddress,
    verificationNumber: verificationNumber || '',
    invoiceNumber: invoiceCoverage,
    invoiceNumbers: invoiceCoverage,
    paymentDate,
    workRowsHtml,
    totalLabour: money(totalLabour),
    totalMaterials: money(totalMaterials),
    otherAdjustments: money(0),
    grossSubjectToCis: money(totalLabour),
    // Use ASCII markers to avoid font-dependent checkbox glyph issues in PDFs.
    cisRate0: rate0 ? '[X]' : '[ ]',
    cisRate20: rate20 ? '[X]' : '[ ]',
    cisRate30: rate30 ? '[X]' : '[ ]',
    cisTaxDeducted: money(totalCis),
    totalGrossPayment: money(totalGross),
    lessCisDeduction: money(totalCis),
    netAmountPaid: money(totalNet),
    generationDate,
    logoPath: cfg?.logoPath || '',
  };

  let rendered = template;
  // Simple variable replacement (supports both {{var}} and {{{var}}})
  for (const [k, v] of Object.entries(replacements)) {
    const safe = String(v ?? '');
    rendered = rendered.replace(new RegExp(`\\{\\{\\{\\s*${k}\\s*\\}\\}\\}`, 'g'), safe);
    rendered = rendered.replace(new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, 'g'), safe);
  }
  // Conditional blocks (very small subset of Handlebars used in templates)
  const conditionalKeys = ['logoPath', 'invoiceNumber', 'invoiceNumbers', 'verificationNumber'];
  for (const key of conditionalKeys) {
    const value = String(replacements[key] || '').trim();
    rendered = rendered.replace(
      new RegExp(`\\{\\{#${key}\\}\\}([\\s\\S]*?)\\{\\{\\/${key}\\}\\}`, 'g'),
      value ? '$1' : ''
    );
  }
  rendered = cleanupRemainingTemplateVariables(rendered);

  const exportBase = `CIS Certificate - ${subcontractorName} - ${period.period_start} to ${period.period_end}`;
  const exportFileName = `${exportBase.replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim()}.pdf`;

  const cfgLogoPath = String(cfg?.logoPath || '').trim();
  const cfgLogoPathAbs = toAbsoluteAssetUrl(cfgLogoPath);
  const exportLogoHtml = (() => {
    const p = cfgLogoPathAbs || cfgLogoPath;
    if (!p) return '';
    // Avoid duplicating if the template already references the logo path.
    if ((cfgLogoPath && rendered.includes(cfgLogoPath)) || (cfgLogoPathAbs && rendered.includes(cfgLogoPathAbs))) return '';
    return `<div class="export-logo"><img src="${p}" alt="Company Logo" /></div>`;
  })();

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(exportBase)}</title>
    <base href="${window.location.origin}/" />
    <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
    <style>
      /* Keep only page size here; PDF margins come from pdfOptions to avoid overlap with header/footer templates. */
      @page { size: A4; }
      html, body { height: 100%; margin: 0; padding: 0; }
      body {
        display: flex;
        min-height: 100vh;
        flex-direction: column;
        font-family: ${cfg?.fontFamily || 'Poppins, Arial, sans-serif'};
        font-size: ${cfg?.fontSizeBase || 14}px;
        color: #111827;
      }
      #main-content { flex: 1 1 auto; }
      #doc-footer { flex: 0 0 auto; text-align: center; padding: 12px 0 6px 0; margin-top: 12px; }
      #doc-footer img { max-width: 100%; height: auto; max-height: 150px; object-fit: contain; }

      .header-image { text-align: center; margin: 0 0 8px 0; }
      .header-image img { display: block; margin: 0 auto; max-width: 100%; height: auto; max-height: 150px; object-fit: contain; }

      .export-logo { display: flex; justify-content: flex-end; margin: 0 0 12px 0; }
      .export-logo img { width: ${cfg?.logoWidth || 200}px; height: ${cfg?.logoHeight || 100}px; object-fit: contain; }

      .header { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; border-bottom:2px solid ${cfg?.headerColor || '#16a34a'}; padding-bottom:8px; margin-bottom:12px; }
      .title { font-size: 20px; font-weight: 700; color: ${cfg?.primaryColor || '#16a34a'}; }
      .subtitle { font-size: 14px; color: ${cfg?.secondaryColor || '#475569'}; margin-top: 4px; }
      .date { font-size: 12px; color: ${cfg?.secondaryColor || '#475569'}; margin-top: 6px; }
      .logo { width: ${cfg?.logoWidth || 200}px; height: ${cfg?.logoHeight || 100}px; object-fit: contain; }
      .info-grid { display:flex; gap:20px; margin: 12px 0; }
      .info-section { flex: 1; }
      .info-section h3 { margin: 0 0 8px 0; font-size: 12px; text-transform: uppercase; color: ${cfg?.secondaryColor || '#475569'}; letter-spacing: .04em; }
      .info-section p { margin: 0; line-height: 1.45; font-size: 12.5px; }
      .description-section { margin-top: 10px; }
      .description-section h3 { margin: 0 0 6px 0; font-size: 12px; text-transform: uppercase; color: ${cfg?.secondaryColor || '#475569'}; letter-spacing: .04em; }
      .description-section p { margin: 0; line-height: 1.5; font-size: 12.5px; }
      table.items-table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 12.5px; }
      table.items-table th, table.items-table td { border: 1px solid #e5e7eb; padding: 8px; vertical-align: top; }
      table.items-table th { background: #f8fafc; text-align: left; }
    </style>
  </head><body>
    <main id="main-content">
      ${exportLogoHtml}
      ${rendered}
    </main>
  </body></html>`;

  const pdfOptions = buildPdfHeaderFooterOptions({
    headerImagePath: cfg?.headerImagePath,
    footerImagePath: cfg?.footerImagePath,
    footerHeightPx: await pdfFooterHeightPx(cfg?.footerImagePath),
  });

  // Generate a true PDF server-side via local export endpoint.
  const response = await fetch('/api/export-pdf', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ html, pdfOptions })
  });
  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || `PDF export failed (${response.status})`);
  }

  const pdfBlob = await response.blob();
  const pdfUrl = URL.createObjectURL(pdfBlob);
  const anchor = document.createElement('a');
  anchor.href = pdfUrl;
  anchor.download = exportFileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(pdfUrl);
}

interface CISViewProps {
  className?: string;
  organisationId?: string;
}

const CISView: React.FC<CISViewProps> = ({ className, organisationId }) => {
  const [cisSummary, setCisSummary] = useState<CISSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedPeriods, setExpandedPeriods] = useState<Set<string>>(new Set());
  const [expandedInvoices, setExpandedInvoices] = useState<Set<string>>(new Set());

  const [certModalOpen, setCertModalOpen] = useState(false);
  const [certPeriod, setCertPeriod] = useState<CISMonthlyPeriod | null>(null);
  const [certSubId, setCertSubId] = useState<string>('');
  const [certVerificationNumber, setCertVerificationNumber] = useState<string>('');
  const [certGenerating, setCertGenerating] = useState(false);
  const [certError, setCertError] = useState<string | null>(null);

  useEffect(() => {
    loadCISData();
  }, []);

  const loadCISData = async () => {
    try {
      setLoading(true);
      setError(null);
      const summary = await cisService.getCISSummary();
      setCisSummary(summary);
      
      // Auto-expand the most recent period
      if (summary.monthly_periods.length > 0) {
        setExpandedPeriods(new Set([summary.monthly_periods[0].period_start]));
      }
    } catch (err: any) {
      console.error('Failed to load CIS data:', err);
      setError(err.message || 'Failed to load CIS data');
    } finally {
      setLoading(false);
    }
  };

  const togglePeriodExpansion = (periodStart: string) => {
    const newExpanded = new Set(expandedPeriods);
    if (newExpanded.has(periodStart)) {
      newExpanded.delete(periodStart);
    } else {
      newExpanded.add(periodStart);
    }
    setExpandedPeriods(newExpanded);
  };

  const toggleInvoiceExpansion = (invoiceId: string) => {
    const newExpanded = new Set(expandedInvoices);
    if (newExpanded.has(invoiceId)) {
      newExpanded.delete(invoiceId);
    } else {
      newExpanded.add(invoiceId);
    }
    setExpandedInvoices(newExpanded);
  };

  const openCertificateModal = (period: CISMonthlyPeriod) => {
    setCertError(null);
    setCertPeriod(period);
    setCertSubId('');
    setCertVerificationNumber('');
    setCertModalOpen(true);
  };

  const periodSubOptions = useMemo(() => {
    const p = certPeriod;
    if (!p) return [];
    const byId = new Map<string, { id: string; label: string }>();
    for (const inv of p.invoices || []) {
      const id = String(inv.subcontractor_id || '').trim();
      if (!id) continue;
      const label = [inv.subcontractor_name, inv.subcontractor_company].filter(Boolean).join(' — ');
      if (!byId.has(id)) byId.set(id, { id, label: label || 'Unknown subcontractor' });
    }
    return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [certPeriod]);

  const exportCisCertificate = async () => {
    if (!certPeriod) return;
    if (!certSubId) {
      setCertError('Please select a subcontractor.');
      return;
    }
    try {
      setCertGenerating(true);
      setCertError(null);

      await downloadCisCertificate({
        period: certPeriod,
        subcontractorId: certSubId,
        verificationNumber: certVerificationNumber,
        organisationId,
      });

      setCertModalOpen(false);
    } catch (e: any) {
      console.error(e);
      setCertError(e?.message || 'Failed to generate certificate');
    } finally {
      setCertGenerating(false);
    }
  };

  const renderInvoiceLineItems = (invoice: CISInvoiceItem) => {
    if (!invoice.line_items || invoice.line_items.length === 0) {
      return (
        <Typography level="body-sm" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
          No line items available
        </Typography>
      );
    }

    return (
      <Box sx={{ mt: 1 }}>
        <Table size="sm">
        <thead>
          <tr>
            <th style={{ width: '50%' }}>Description</th>
            <th style={{ width: '15%', textAlign: 'right' }}>Qty</th>
            <th style={{ width: '15%', textAlign: 'right' }}>Unit Price</th>
            <th style={{ width: '20%', textAlign: 'right' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {invoice.line_items.map((item, idx) => (
            <tr key={idx}>
              <td>{item.description || '—'}</td>
              <td style={{ textAlign: 'right' }}>{item.quantity || 0}</td>
              <td style={{ textAlign: 'right' }}>{currency(item.unit_price || 0)}</td>
              <td style={{ textAlign: 'right' }}>{currency(item.total || 0)}</td>
            </tr>
          ))}
        </tbody>
        </Table>
      </Box>
    );
  };

  const renderInvoice = (invoice: CISInvoiceItem) => {
    const isExpanded = expandedInvoices.has(invoice.id);
    
    const breakdown = getInvoiceLabourMaterials(invoice);
    return (
      <Card key={invoice.id} variant="outlined" sx={{ mb: 1 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton
                size="sm"
                variant="plain"
                onClick={() => toggleInvoiceExpansion(invoice.id)}
              >
                <IconWrapper icon={isExpanded ? FaChevronDown : FaChevronRight} />
              </IconButton>
              <Box>
                <Typography level="body-sm">
                  Invoice #{invoice.invoice_number || 'N/A'} • {invoice.invoice_date}
                  {invoice.to_company_name && ` • ${invoice.to_company_name}`}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography level="body-sm">{currency(invoice.subtotal)}</Typography>
              <Typography level="body-sm" sx={{ color: 'warning.500' }}>
                -{currency(invoice.cis_deduction)} ({invoice.cis_percent}%)
              </Typography>
              <Typography level="title-sm" sx={{ color: 'success.500' }}>
                {currency(invoice.net_total)}
              </Typography>
            </Stack>
          </Stack>
          
          {isExpanded && (
            <Box sx={{ mt: 2 }}>
              {invoice.description && (
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  <strong>Description:</strong> {invoice.description}
                </Typography>
              )}
              <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                  Labour: {currency(breakdown.labour)}
                </Typography>
                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                  Materials: {currency(breakdown.materials)}
                </Typography>
              </Stack>
              {renderInvoiceLineItems(invoice)}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderSubcontractorGroup = (subcontractorName: string, invoices: CISInvoiceItem[]) => {
    const totalGross = invoices.reduce((sum, inv) => sum + inv.subtotal, 0);
    const totalCisDeduction = invoices.reduce((sum, inv) => sum + inv.cis_deduction, 0);
    const totalNet = invoices.reduce((sum, inv) => sum + inv.net_total, 0);
    
    return (
      <Box key={subcontractorName} sx={{ mb: 2 }}>
        {/* Subcontractor Header with Cumulative Totals */}
        <Card variant="soft" color="neutral" sx={{ mb: 1 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography level="title-md">
                {subcontractorName}
              </Typography>
              <Stack direction="row" spacing={3} alignItems="center">
                <Box sx={{ textAlign: 'center' }}>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Gross</Typography>
                  <Typography level="title-sm">{currency(totalGross)}</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>CIS Deduction</Typography>
                  <Typography level="title-sm" sx={{ color: 'warning.500' }}>
                    -{currency(totalCisDeduction)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Net Total</Typography>
                  <Typography level="title-md" sx={{ color: 'success.500' }}>
                    {currency(totalNet)}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
        
        {/* Individual Invoices */}
        {invoices.map(renderInvoice)}
      </Box>
    );
  };

  const renderMonthlyPeriod = (period: CISMonthlyPeriod) => {
    const isExpanded = expandedPeriods.has(period.period_start);
    
    // Group invoices by subcontractor
    const groupedBySubcontractor = period.invoices.reduce((groups, invoice) => {
      const subcontractorName = invoice.subcontractor_company || invoice.subcontractor_name;
      if (!groups[subcontractorName]) {
        groups[subcontractorName] = [];
      }
      groups[subcontractorName].push(invoice);
      return groups;
    }, {} as Record<string, CISInvoiceItem[]>);
    
    return (
      <Card key={period.period_start} variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton
                size="sm"
                variant="plain"
                onClick={() => togglePeriodExpansion(period.period_start)}
              >
                <IconWrapper icon={isExpanded ? FaChevronDown : FaChevronRight} />
              </IconButton>
              <Box>
                <Typography level="title-md">
                  {period.period_label}
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                  {period.invoices.length} invoice{period.invoices.length !== 1 ? 's' : ''} • 
                  {period.period_start} to {period.period_end}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography level="body-sm">{currency(period.total_gross)}</Typography>
              <Typography level="body-sm" sx={{ color: 'warning.500' }}>
                -{currency(period.total_cis_deduction)}
              </Typography>
              <Typography level="title-md" sx={{ color: 'success.500' }}>
                {currency(period.total_net)}
              </Typography>
              <Button
                size="sm"
                color="success"
                variant="solid"
                startDecorator={<IconWrapper icon={FaDownload} />}
                onClick={() => openCertificateModal(period)}
              >
                Export certificate
              </Button>
            </Stack>
          </Stack>
          
          {isExpanded && (
            <Box sx={{ mt: 2 }}>
              {Object.entries(groupedBySubcontractor).map(([subcontractorName, invoices]) =>
                renderSubcontractorGroup(subcontractorName, invoices)
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box className={className}>
        <Typography level="h4" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <IconWrapper icon={FaFileInvoice} />
          <Box sx={{ ml: 1 }}>CIS Submissions</Box>
        </Typography>
        <Typography level="body-md">Loading CIS data...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={className}>
        <Typography level="h4" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <IconWrapper icon={FaFileInvoice} />
          <Box sx={{ ml: 1 }}>CIS Submissions</Box>
        </Typography>
        <Typography level="body-md" color="danger">
          Error: {error}
        </Typography>
      </Box>
    );
  }

  if (!cisSummary || cisSummary.monthly_periods.length === 0) {
    return (
      <Box className={className}>
        <Typography level="h4" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <IconWrapper icon={FaFileInvoice} />
          <Box sx={{ ml: 1 }}>CIS Submissions</Box>
        </Typography>
        <Typography level="body-md" sx={{ color: 'text.tertiary' }}>
          No CIS invoices found. CIS invoices are those with CIS percentage greater than 0%.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={className}>
      <Typography level="h4" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <IconWrapper icon={FaFileInvoice} />
        <Box sx={{ ml: 1 }}>CIS Submissions</Box>
      </Typography>
      
      <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
        Construction Industry Scheme submissions grouped by HMRC monthly periods (6th to 5th of next month, inclusive)
      </Typography>

      {/* Grand Totals */}
      <Card variant="soft" color="primary" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography level="title-lg">Grand Totals</Typography>
            <Stack direction="row" spacing={3} alignItems="center">
              <Box sx={{ textAlign: 'center' }}>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Gross</Typography>
                <Typography level="title-md">{currency(cisSummary.grand_total_gross)}</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>CIS Deduction</Typography>
                <Typography level="title-md" sx={{ color: 'warning.500' }}>
                  -{currency(cisSummary.grand_total_cis_deduction)}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Net Total</Typography>
                <Typography level="title-lg" sx={{ color: 'success.500' }}>
                  {currency(cisSummary.grand_total_net)}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Monthly Periods */}
      {cisSummary.monthly_periods.map(renderMonthlyPeriod)}

      <Modal open={certModalOpen} onClose={() => setCertModalOpen(false)}>
        <ModalDialog>
          <DialogTitle>Export CIS certificate</DialogTitle>
          <DialogContent>
            <Stack spacing={1.25}>
              {certPeriod && (
                <Alert color="neutral" variant="soft">
                  Period: <strong>{certPeriod.period_label}</strong> ({certPeriod.period_start} to {certPeriod.period_end})
                </Alert>
              )}
              {certError && (
                <Alert color="danger" variant="soft">{certError}</Alert>
              )}
              <FormControl>
                <FormLabel>Subcontractor</FormLabel>
                <Select
                  value={certSubId}
                  onChange={(_, v) => {
                    const id = String(v || '');
                    setCertSubId(id);
                    // Pre-fill from the account; it can still be typed over
                    const match = (certPeriod?.invoices || []).find((inv) => String(inv.subcontractor_id) === id);
                    setCertVerificationNumber(match?.subcontractor_verification_number || '');
                  }}
                  placeholder="Select subcontractor"
                >
                  {periodSubOptions.map(s => (
                    <Option key={s.id} value={s.id}>{s.label}</Option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Verification Number (optional)</FormLabel>
                <Input
                  value={certVerificationNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCertVerificationNumber(e.target.value)}
                  placeholder="e.g. HMRC verification ref"
                />
              </FormControl>
              <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                Exports a true PDF using the server-side PDF renderer.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="plain" onClick={() => setCertModalOpen(false)} disabled={certGenerating}>Cancel</Button>
            <Button color="success" onClick={exportCisCertificate} loading={certGenerating}>
              Generate
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default CISView;
